import { useMemo } from 'react';
import { getMapBy } from '../../../common/utils';
import { useGetAlgorithmMappingsQuery } from '../mappings/query/useGetAlgorithmMappingsQuery';
import { useGetAlgorithmSourcesQuery } from '../sources/query/useGetAlgorithmSourcesQuery';
import type { AlgorithmSourceDimensionFormDataType } from '../sources/schema/types';
import { useGetAlgorithmJoinsQuery } from './query/useGetAlgorithmJoinsQuery';

/**
 * Fetches algorithm joins, sources and mappings data
 * @param id number - Algorithm ID
 */
export const useAlgorithmJoinsDataQuery = (id: number) => {
  const {
    data: algorithmJoins,
    isFetching: isFetchingJoins,
    refetch: refetchJoins,
  } = useGetAlgorithmJoinsQuery({ id });

  const { data: algorithmSources, isFetching: isFetchingSources } =
    useGetAlgorithmSourcesQuery({ id });

  const {
    data: algorithmMappings,
    isFetching: isFetchingMappings,
    refetch: refetchMappings,
  } = useGetAlgorithmMappingsQuery({ id });

  const data = useMemo(
    () =>
      algorithmJoins &&
      algorithmSources &&
      algorithmMappings && {
        ...algorithmJoins,
        ...algorithmSources,
        ...algorithmMappings,
        // A two-level map: algorithmSourceId -> dimensionCode -> AlgorithmSourceDimensionFormDataType
        sourceDimensionsMap: getMapBy(
          algorithmSources.sources,
          'id',
          (source) => getMapBy(source.dimensions, 'code'),
        ) as SourceDimensionsMap,
      },
    [algorithmJoins, algorithmSources, algorithmMappings],
  );

  return {
    isFetchingJoins,
    isFetchingSources,
    isFetchingMappings,
    data,
    isFetching: isFetchingJoins || isFetchingSources || isFetchingMappings,
    refetchJoins,
    refetchMappings,
  };
};

export type SourceDimensionsMap = Map<
  number,
  Map<string, AlgorithmSourceDimensionFormDataType>
>;
